import { startTransition, useEffect, useMemo, useState } from "react";
import { useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { CourseSearchRecord, DepartmentRecord } from "../../shared/types";
import { StaticDataState } from "../components/StaticDataState";
import { formatCourseOfferingPreview } from "../lib/offerings";
import { prepareCourseSearchEntries, searchCourseEntries } from "../lib/search";
import { fetchCourseSearchIndex, fetchDepartments } from "../lib/static-data";
import { useDebouncedValue } from "../lib/use-debounced-value";
import { useInfiniteResults } from "../lib/use-infinite-results";

const DEFAULT_VISIBLE_COURSES = 24;
const SEARCH_BATCH_SIZE = 60;

export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const [courses, setCourses] = useState<CourseSearchRecord[]>([]);
  const [departments, setDepartments] = useState<DepartmentRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState(params.get("q") ?? "");
  const [department, setDepartment] = useState(params.get("department") ?? "");
  const trimmedQuery = query.trim();
  const debouncedQuery = useDebouncedValue(trimmedQuery, 180);

  useEffect(() => {
    setError(null);
    void Promise.all([fetchCourseSearchIndex(), fetchDepartments()])
      .then(([nextCourses, nextDepartments]) => {
        startTransition(() => {
          setCourses(nextCourses);
          setDepartments(nextDepartments);
        });
      })
      .catch((nextError) => {
        setError(nextError instanceof Error ? nextError.message : "Could not load course search.");
      });
  }, [reloadKey]);

  useEffect(() => {
    function focusSearch(event: KeyboardEvent) {
      const target = event.target;
      const isEditableTarget = target instanceof HTMLInputElement
        || target instanceof HTMLTextAreaElement
        || target instanceof HTMLSelectElement
        || (target instanceof HTMLElement && target.isContentEditable);
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey || isEditableTarget) {
        return;
      }
      event.preventDefault();
      inputRef.current?.focus();
    }

    document.addEventListener("keydown", focusSearch);
    return () => document.removeEventListener("keydown", focusSearch);
  }, []);

  useEffect(() => {
    setQuery(params.get("q") ?? "");
    setDepartment(params.get("department") ?? "");
  }, [params]);

  const preparedCourses = useMemo(() => prepareCourseSearchEntries(courses), [courses]);
  const hasActiveFilters = Boolean(debouncedQuery || department);
  const searchResults = useMemo(() => {
    if (!hasActiveFilters) {
      return {
        results: courses.slice(0, DEFAULT_VISIBLE_COURSES),
        totalMatches: courses.length,
      };
    }

    return searchCourseEntries(preparedCourses, debouncedQuery, department);
  }, [courses, debouncedQuery, department, hasActiveFilters, preparedCourses]);
  const {
    anchorRef,
    hasMore,
    loadMore,
    visibleCount,
    visibleItems: visibleCourses,
  } = useInfiniteResults(
    searchResults.results,
    hasActiveFilters ? SEARCH_BATCH_SIZE : DEFAULT_VISIBLE_COURSES,
    SEARCH_BATCH_SIZE,
  );
  const isUpdatingResults = trimmedQuery !== debouncedQuery;
  const selectedDepartment = departments.find((candidate) => candidate.code === department);

  if (error) {
    return <StaticDataState title="Course search could not load." message={error} error onRetry={() => setReloadKey((value) => value + 1)} />;
  }

  if (courses.length === 0 && departments.length === 0) {
    return <StaticDataState title="Loading course search..." />;
  }

  return (
    <section className="stack-lg">
      <div className="panel stack-md">
        <div className="section-heading">
          <p className="eyebrow">Course Search</p>
          <h1>Search Dartmouth courses</h1>
        </div>
        <p className="search-note">
          Looking for a professor instead? Use the dedicated <Link to="/professors/">professor search</Link>. Press <kbd>/</kbd> to focus search.
        </p>
        {selectedDepartment ? (
          <p className="notice-banner">
            Filtering within {selectedDepartment.name || selectedDepartment.code}.
          </p>
        ) : null}
        <form
          className="search-bar course-search-bar"
          onSubmit={(event) => {
            event.preventDefault();
            const nextParams = new URLSearchParams();
            if (query.trim()) {
              nextParams.set("q", query.trim());
            }
            if (department) {
              nextParams.set("department", department);
            }
            setParams(nextParams);
          }}
        >
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Try ECON001, economics 1, ECON 1, COSC, or a course title"
          />
          <select value={department} onChange={(event) => setDepartment(event.target.value)}>
            <option value="">All departments</option>
            {departments.map((departmentOption) => (
              <option key={departmentOption.code} value={departmentOption.code}>
                {departmentOption.code} - {departmentOption.name}
              </option>
            ))}
          </select>
          <button type="submit">Search</button>
        </form>
      </div>

      {hasActiveFilters ? (
        <p className="toolbar-summary">
          {isUpdatingResults
            ? "Updating results..."
            : `${searchResults.totalMatches} course${searchResults.totalMatches === 1 ? "" : "s"} found`}
        </p>
      ) : null}

      <div className="results-grid">
        {visibleCourses.map((course) => {
          const offeringPreview = formatCourseOfferingPreview(course);

          return (
            <Link key={course.slug} className="result-card" to={`/course/${course.slug}/`}>
              <strong>{course.shortName}</strong>
              <span>{course.title}</span>
              <small>{course.departmentName || course.department}</small>
              {offeringPreview ? <small>{offeringPreview}</small> : null}
              {course.professorNames.length > 0 ? (
                <small>Professors: {course.professorNames.slice(0, 3).join(", ")}</small>
              ) : null}
              <em>{course.legacyReviewCount} reviews</em>
            </Link>
          );
        })}
      </div>
      {hasMore ? (
        <div className="results-footer">
          <p className="toolbar-summary">
            Showing {visibleCount} of {searchResults.totalMatches} courses
          </p>
          <button type="button" className="ghost-button" onClick={loadMore}>
            Load more
          </button>
          <div ref={anchorRef} className="results-sentinel" aria-hidden="true" />
        </div>
      ) : null}
      {visibleCourses.length === 0 ? (
        <p className="notice-banner">No courses match that search yet. Try a department code, course number, title, or a nearby spelling.</p>
      ) : null}
    </section>
  );
}
